import { PageHero } from "@/components/PageHero";
import { Reveal, Kicker, Tag } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";

const RIGHTS = ["Culture", "Family", "Community", "Identity", "Belonging"];
const CONNECTIONS = [
  "Family", "Kin", "Elders", "Community", "Country",
  "Aboriginal Community Controlled Organisations", "Appropriate cultural knowledge holders",
];

const Culture = () => (
  <div data-testid="culture-page">
    <PageHero
      kicker="Cultural Identity & Connection"
      title={"Culture, family, community,\nidentity and belonging."}
      lede="Aboriginal children have a right to culture, family, community, identity and belonging. Ngawilyi Narrative is Aboriginal-owned and grounded in Aboriginal ways of knowing, being and doing."
    />

    <section className="relative px-6 py-24 sm:py-32 overflow-hidden grain">
      <img
        src="https://images.pexels.com/photos/7605676/pexels-photo-7605676.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
        alt="Warm abstract texture — placeholder for community-led imagery"
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="culture-texture"
      />
      <div className="absolute inset-0 bg-charcoal/85" />
      <div className="relative max-w-5xl mx-auto text-cream">
        <Reveal>
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-ochre mb-8">The name we carry</p>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08]">
            Ngawilyi means <span className="italic text-ochre">"Aunty"</span> in Arabana language.
          </h2>
          <p className="mt-8 text-base lg:text-lg leading-relaxed text-cream/85 max-w-3xl">
            The use of Arabana language and authorisation for the program and name has been provided through
            Arabana Elders and the Arabana Aboriginal Corporation. We hold this cultural authority with deep
            respect and gratitude.
          </p>
          <div className="mt-8"><Tag tone="ochre">Authorised through Arabana Elders & the Arabana Aboriginal Corporation</Tag></div>
        </Reveal>
      </div>
    </section>

    <section className="px-6 py-14 sm:py-20 bg-cream">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
        <Reveal>
          <Kicker>Every Nation, its own authority</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
            We do not claim cultural authority over <span className="italic text-terracotta">another person's Country.</span>
          </h2>
          <p className="mt-8 text-base lg:text-lg leading-relaxed text-earth">
            Cultural connection should respect each person's own Nation and cultural authority. When working
            alongside Aboriginal children and families from other Nations, Ngawilyi supports connection back to
            their own family, community, Elders, Country and cultural knowledge holders — never positioning
            ourselves in their place.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="rounded-[40px] bg-creamdark border border-ochre/25 p-10 sm:p-12">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-terracotta mb-6">Where appropriate, we support connection back to</p>
            <ul className="space-y-4">
              {CONNECTIONS.map((c, i) => (
                <li key={c} className="flex gap-4 items-center">
                  <span className="font-display italic text-terracotta text-xl">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-earth font-medium">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="px-6 pb-14 sm:pb-20 bg-cream">
      <Reveal>
        <div className="max-w-7xl mx-auto rounded-[48px] bg-blush/40 p-10 sm:p-16 grain relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-terracotta/15 organic-blob blur-2xl" />
          <div className="relative">
            <Kicker>A right, not a program add-on</Kicker>
            <div className="flex flex-wrap gap-x-10 gap-y-4 mt-6">
              {RIGHTS.map((r) => (
                <p key={r} className="font-display font-light text-4xl sm:text-5xl text-charcoal">{r}<span className="text-terracotta">.</span></p>
              ))}
            </div>
            <p className="mt-8 text-earth/85 max-w-2xl leading-relaxed">
              Culture is woven through every part of the Ngawilyi model — story, movement, mentoring and community —
              because identity and belonging are not separate from wellbeing.
            </p>
          </div>
        </div>
      </Reveal>
    </section>

    <CTABand
      title="Culture is strongest when community leads it. Help shape how Ngawilyi works."
      primary={{ to: "/consultation", label: "Join a Community Yarn" }}
      secondary={{ to: "/about", label: "Read Our Story" }}
    />
  </div>
);

export default Culture;
